import { useCallback, useRef } from 'react'
import { Consult } from 'app/models/domain/consult'
import { logger } from 'app/logging/Logger'
import { consultTemplateRepository } from 'app/api/consult/repository/ConsultTemplateRepository'
import { consultPatientRepository } from 'app/api/consult/repository/ConsultPatientRepository'
import { entityRepository } from 'app/api/consult/repository/EntityRepository'
import { additionalTemplateRepository } from 'app/api/consult/repository/AdditionalTemplateRepository'
import { AmplifyDiarizationRepository } from 'app/api/consult/repository/DiarizationRepository'
import { AmplifyTranscriptionRepository } from 'app/api/consult/repository/TranscriptionRepository'

export interface EnrichmentOptions {
    template?: boolean
    entities?: boolean
    patient?: boolean
    additionalTemplates?: boolean
    diarization?: boolean
    transcription?: boolean
}

export interface UseConsultEnrichmentResult {
    enrichConsult(
        consult: Consult,
        options?: EnrichmentOptions
    ): Promise<Consult>
    enrichBatch(
        consults: Consult[],
        batchSize: number,
        onBatchComplete: (consults: Consult[], isComplete: boolean) => void
    ): Promise<void>
    reEnrich(consult: Consult, options?: EnrichmentOptions): Promise<Consult>
    /**
     * Re-fetch templates that have null content. This handles the case where
     * a template or additional template was created but its content hasn't
     * been populated yet. Returns the consult with refreshed template data.
     */
    reloadNullTemplates(consult: Consult): Promise<Consult>
}

const DEFAULT_OPTIONS: EnrichmentOptions = {
    patient: true,
    entities: true,
}

const diarizationRepository = new AmplifyDiarizationRepository()
const transcriptionRepository = new AmplifyTranscriptionRepository()

/**
 * Serial async queue that ensures only one enrichment task runs at a time.
 * When a new page arrives while the previous page's enrichment is still running,
 * the new page's enrichment is queued and will begin after the current one completes.
 */
export class EnrichmentQueue {
    private queue: Array<() => Promise<void>> = []
    private running = false

    async enqueue(task: () => Promise<void>): Promise<void> {
        return new Promise<void>((resolve) => {
            this.queue.push(async () => {
                await task()
                resolve()
            })
            if (!this.running) this.processNext()
        })
    }

    private async processNext(): Promise<void> {
        if (this.queue.length === 0) {
            this.running = false
            return
        }
        this.running = true
        const task = this.queue.shift()!
        await task()
        this.processNext()
    }
}

/**
 * Load a single enrichment field for a consult.
 * Returns silently on failure — errors are logged, not thrown.
 */
async function loadField(
    consult: Consult,
    field: keyof EnrichmentOptions
): Promise<void> {
    const id = consult.id
    switch (field) {
        case 'template': {
            const template = await consultTemplateRepository.getByConsultId(id)
            // Backward compat: keep embedded template if separate-table returns null
            if (template !== null) {
                consult.template = template
                consult.webContent = template.webContent
            }
            break
        }
        case 'entities': {
            const entities = await entityRepository.getByConsultId(id)
            // Backward compat: keep embedded entities if separate-table returns null
            if (entities !== null) {
                consult.entities = entities
            }
            break
        }
        case 'patient': {
            const patientData =
                await consultPatientRepository.getByConsultId(id)
            // Backward compat: keep embedded patient fields if separate-table returns null
            if (patientData !== null) {
                consult.patientId = patientData.patientId ?? consult.patientId
                consult.attachedPatient =
                    patientData.attachedPatient ?? consult.attachedPatient
                consult.matchedPatients =
                    patientData.matchedPatients?.filter(
                        (p): p is NonNullable<typeof p> => Boolean(p)
                    ) ?? consult.matchedPatients
            }
            break
        }
        case 'additionalTemplates': {
            const additionalData =
                await additionalTemplateRepository.getByConsultId(id)
            // Backward compat: keep embedded additional templates if separate-table returns null
            if (additionalData !== null) {
                consult.additionalTemplates =
                    additionalData.additionalTemplates ??
                    consult.additionalTemplates
            }
            break
        }
        case 'diarization': {
            const diarizationData =
                await diarizationRepository.getByConsultId(id)
            // Backward compat: keep embedded diarization if separate-table returns null
            if (diarizationData !== null) {
                consult.diarization =
                    diarizationData.diarization ?? consult.diarization
            }
            break
        }
        case 'transcription': {
            const transcriptionData =
                await transcriptionRepository.getByConsultId(id)
            // Backward compat: keep embedded cloudTranscription if separate-table returns null
            if (transcriptionData !== null) {
                consult.transcription = transcriptionData
            }
            break
        }
    }
}

/**
 * Enrich a single consult with the requested fields using Promise.allSettled.
 * Never throws — failed fields retain their previous values.
 */
async function performEnrichment(
    consult: Consult,
    options: EnrichmentOptions
): Promise<Consult> {
    const fields = (Object.keys(options) as (keyof EnrichmentOptions)[]).filter(
        (k) => options[k]
    )

    const results = await Promise.allSettled(
        fields.map((field) => loadField(consult, field))
    )

    results.forEach((result, i) => {
        if (result.status === 'rejected') {
            logger.error(
                `Enrichment failed for field: ${fields[i]}`,
                result.reason instanceof Error
                    ? result.reason
                    : new Error(String(result.reason)),
                {
                    action: 'enrichment_field_error',
                    metadata: { consultId: consult.id, field: fields[i] },
                }
            )
        }
    })

    return consult
}

export function useConsultEnrichment(): UseConsultEnrichmentResult {
    const queueRef = useRef(new EnrichmentQueue())

    const enrichConsult = useCallback(
        async (
            consult: Consult,
            options?: EnrichmentOptions
        ): Promise<Consult> => {
            return performEnrichment(consult, options ?? DEFAULT_OPTIONS)
        },
        []
    )

    const enrichBatch = useCallback(
        async (
            consults: Consult[],
            batchSize: number,
            onBatchComplete: (consults: Consult[], isComplete: boolean) => void
        ): Promise<void> => {
            await queueRef.current.enqueue(async () => {
                if (consults.length === 0) {
                    onBatchComplete(consults, true)
                    return
                }

                for (let i = 0; i < consults.length; i += batchSize) {
                    const batch = consults.slice(i, i + batchSize)
                    const isFinalBatch = i + batchSize >= consults.length

                    await Promise.allSettled(
                        batch.map((c) => performEnrichment(c, DEFAULT_OPTIONS))
                    )

                    onBatchComplete(consults, isFinalBatch)
                }
            })
        },
        []
    )

    const reEnrich = useCallback(
        async (
            consult: Consult,
            options?: EnrichmentOptions
        ): Promise<Consult> => {
            return performEnrichment(consult, options ?? DEFAULT_OPTIONS)
        },
        []
    )

    const reloadNullTemplates = useCallback(
        async (consult: Consult): Promise<Consult> => {
            const emptyAdditionalTemplates =
                consult.additionalTemplates?.filter(
                    (template) => template.content === null
                )?.length ?? 0

            if (emptyAdditionalTemplates > 0) {
                logger.info(
                    '[enrichment] Reloading null additional templates',
                    {
                        action: 'reload_null_additional_templates',
                        metadata: {
                            consultId: consult.id,
                            emptyCount: emptyAdditionalTemplates,
                        },
                    }
                )
                return performEnrichment(consult, { additionalTemplates: true })
            } else if (consult.template?.content === null) {
                logger.info('[enrichment] Reloading null primary template', {
                    action: 'reload_null_template',
                    metadata: { consultId: consult.id },
                })
                return performEnrichment(consult, { template: true })
            }

            return consult
        },
        []
    )

    return { enrichConsult, enrichBatch, reEnrich, reloadNullTemplates }
}
